(() => {
  const initialized = new WeakMap();
  const initializedStories = new WeakMap();
  const initializedReviews = new WeakMap();
  const reviewManifestPromises = new Map();

  function initializeProduct(root) {
    if (!root || initialized.has(root)) return;
    const abortController = new AbortController();

    root.querySelectorAll('[data-drdent-product-gallery]').forEach((gallery) => {
      const track = gallery.querySelector('[data-drdent-product-track]');
      const slides = Array.from(gallery.querySelectorAll('[data-drdent-product-slide]'));
      const thumbnails = Array.from(gallery.querySelectorAll('[data-drdent-product-thumbnail]'));
      const arrows = Array.from(gallery.querySelectorAll('[data-drdent-gallery-direction]'));
      let activeIndex = 0;

      function setActive(index, shouldScroll = false) {
        if (!slides.length) return;
        activeIndex = (index + slides.length) % slides.length;
        thumbnails.forEach((thumbnail, thumbnailIndex) => {
          thumbnail.setAttribute('aria-current', thumbnailIndex === activeIndex ? 'true' : 'false');
        });
        if (shouldScroll) {
          slides[activeIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          thumbnails[activeIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }

      function syncFromScroll() {
        if (!track || !slides.length) return;
        const trackLeft = track.getBoundingClientRect().left;
        const nearest = slides.reduce((best, slide, index) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
          return distance < best.distance ? { index, distance } : best;
        }, { index: activeIndex, distance: Number.POSITIVE_INFINITY });
        setActive(nearest.index);
      }

      thumbnails.forEach((button, index) => {
        button.addEventListener('click', () => setActive(index, true), { signal: abortController.signal });
        button.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          const nextIndex = (index + direction + thumbnails.length) % thumbnails.length;
          thumbnails[nextIndex]?.focus();
          setActive(nextIndex, true);
        }, { signal: abortController.signal });
      });

      arrows.forEach((button) => {
        button.addEventListener('click', () => {
          const direction = button.dataset.drdentGalleryDirection === 'next' ? 1 : -1;
          setActive(activeIndex + direction, true);
        }, { signal: abortController.signal });
      });

      track?.addEventListener('scroll', () => requestAnimationFrame(syncFromScroll), { signal: abortController.signal });
      setActive(0);
    });

    initialized.set(root, abortController);
  }

  function initializeWithin(scope = document) {
    scope.querySelectorAll('[data-drdent-product-main]').forEach(initializeProduct);
    scope.querySelectorAll('[data-drdent-product-story]').forEach(initializeStory);
    scope.querySelectorAll('[data-drdent-reviews]').forEach(initializeReviews);
  }

  function initializeStory(root) {
    if (!root || initializedStories.has(root)) return;
    const controller = new AbortController();
    const videos = Array.from(root.querySelectorAll('[data-drdent-product-video]'));
    videos.forEach((video) => {
      if (!video.autoplay) return;
      video.muted = true;
      video.defaultMuted = true;
      video.play().catch(() => {});
    });
    initializedStories.set(root, controller);
  }

  function initializeReviews(root) {
    if (!root || initializedReviews.has(root)) return;
    const controller = new AbortController();
    const ratingControl = root.querySelector('[data-drdent-review-filter]');
    const sortControl = root.querySelector('[data-drdent-review-sort]');
    const moreControl = root.querySelector('[data-drdent-review-more]');
    const count = root.querySelector('[data-drdent-review-count]');
    const status = root.querySelector('[data-drdent-review-status]');
    const list = root.querySelector('[data-drdent-review-list]');
    const state = { ratingFilter: 'all', sort: 'latest', visibleCount: 5, reviews: [] };

    function createReviewCard(review) {
      const card = document.createElement('article');
      card.className = 'drdent-review-card';
      card.dataset.reviewId = review.id;
      card.dataset.rating = String(review.rating);
      card.dataset.date = review.date;
      card.dataset.hasImage = review.image ? 'true' : 'false';
      card.dataset.likes = String(review.likes);

      if (review.image) {
        const template = root.querySelector(`template[data-review-image-template="${CSS.escape(review.image)}"]`);
        if (template) card.append(template.content.cloneNode(true));
      }

      const meta = document.createElement('div');
      meta.className = 'drdent-review-card__meta';
      const author = document.createElement('strong');
      author.textContent = review.author;
      const verified = document.createElement('span');
      verified.className = 'drdent-review-card__verified';
      verified.setAttribute('aria-label', 'Verified customer');
      verified.textContent = '✓';
      const date = document.createElement('time');
      date.dateTime = review.date;
      const [year, month, day] = review.date.split('-').map(Number);
      date.textContent = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(Date.UTC(year, month - 1, day)));
      meta.append(author, verified, date);

      const stars = document.createElement('span');
      stars.className = 'drdent-review-card__stars';
      stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      stars.textContent = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
      const body = document.createElement('p');
      body.className = 'drdent-review-card__body';
      body.textContent = review.body;
      const productLabel = document.createElement('div');
      productLabel.className = 'drdent-review-card__product';
      productLabel.textContent = 'purple-whitening-strips';
      card.append(meta, stars, body, productLabel);
      return card;
    }

    function layoutMasonry() {
      const cards = Array.from(list.children);
      const columns = window.matchMedia('(max-width: 749px)').matches
        ? 1
        : window.matchMedia('(max-width: 989px)').matches ? 2 : 4;
      const gap = 20;
      const width = (list.clientWidth - (gap * (columns - 1))) / columns;
      const heights = Array(columns).fill(0);
      for (const card of cards) {
        card.style.position = 'absolute';
        card.style.width = `${width}px`;
        const column = heights.indexOf(Math.min(...heights));
        card.style.transform = `translate(${column * (width + gap)}px, ${heights[column]}px)`;
        heights[column] += card.getBoundingClientRect().height + gap;
      }
      list.style.height = `${Math.max(0, ...heights) - (cards.length ? gap : 0)}px`;
      list.classList.add('is-ready');
    }

    function render() {
      let filtered = state.reviews.filter((review) => {
        if (state.ratingFilter === 'excellent') return review.rating === 5;
        if (state.ratingFilter === 'good') return review.rating === 4;
        if (state.ratingFilter === 'images') return Boolean(review.image);
        return true;
      });
      filtered = filtered.map((review, index) => ({ review, index })).sort((a, b) => {
        if (state.sort === 'oldest') return a.review.date.localeCompare(b.review.date) || a.index - b.index;
        if (state.sort === 'images') return Number(Boolean(b.review.image)) - Number(Boolean(a.review.image)) || a.index - b.index;
        if (state.sort === 'likes') return b.review.likes - a.review.likes || a.index - b.index;
        return b.review.date.localeCompare(a.review.date) || a.index - b.index;
      });
      const visible = filtered.slice(0, state.visibleCount);
      list.replaceChildren(...visible.map(({ review }) => createReviewCard(review)));
      list.querySelectorAll('img').forEach((image) => {
        image.addEventListener('load', layoutMasonry, { signal: controller.signal, once: true });
      });
      requestAnimationFrame(layoutMasonry);
      const start = filtered.length ? 1 : 0;
      const end = Math.min(state.visibleCount, filtered.length);
      if (count) count.textContent = `Showing ${start} - ${end} of ${filtered.length} reviews`;
      if (moreControl) moreControl.hidden = state.visibleCount >= filtered.length;
    }

    ratingControl?.addEventListener('change', () => {
      state.ratingFilter = ratingControl.value;
      state.visibleCount = 5;
      render();
    }, { signal: controller.signal });
    sortControl?.addEventListener('change', () => {
      state.sort = sortControl.value;
      render();
    }, { signal: controller.signal });
    moreControl?.addEventListener('click', () => {
      state.visibleCount += 5;
      render();
    }, { signal: controller.signal });
    window.addEventListener('resize', layoutMasonry, { signal: controller.signal });
    initializedReviews.set(root, controller);
    if (moreControl) moreControl.disabled = true;

    const manifestPromise = reviewManifestPromises.get(root.dataset.reviewManifestUrl)
      || fetch(root.dataset.reviewManifestUrl, { credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error(`Unable to load reviews: ${response.status}`);
        return response.json();
      });
    reviewManifestPromises.set(root.dataset.reviewManifestUrl, manifestPromise);
    manifestPromise.then((manifest) => {
      if (controller.signal.aborted) return;
      if (manifest.productHandle !== 'violet-teeth-brightening-strips' || manifest.reviews.length !== 120) {
        throw new Error('Invalid product review manifest');
      }
      state.reviews = manifest.reviews;
      if (moreControl) moreControl.disabled = false;
      render();
    }).catch(() => {
      if (controller.signal.aborted) return;
      if (moreControl) moreControl.hidden = true;
      if (status) status.textContent = 'Unable to load more reviews right now.';
    });
  }

  function destroyReviews(root) {
    const controller = root && initializedReviews.get(root);
    if (controller) controller.abort();
    if (root) initializedReviews.delete(root);
  }

  document.addEventListener('DOMContentLoaded', () => initializeWithin(), { once: true });
  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-drdent-product-main]')
      ? event.target
      : event.target.querySelector?.('[data-drdent-product-main]');
    const controller = root && initialized.get(root);
    if (controller) controller.abort();
    if (root) initialized.delete(root);
    for (const [selector, registry] of [
      ['[data-drdent-product-story]', initializedStories],
    ]) {
      const child = event.target.matches?.(selector) ? event.target : event.target.querySelector?.(selector);
      const childController = child && registry.get(child);
      if (childController) childController.abort();
      if (child) registry.delete(child);
    }
    const reviewsRoot = event.target.matches?.('[data-drdent-reviews]')
      ? event.target
      : event.target.querySelector?.('[data-drdent-reviews]');
    destroyReviews(reviewsRoot);
  });
})();
